<template>
<div>
  <LoginModal @loginFinished="loginSucceeded" />
  <RegisterModal @registrationFinished="registrationSucceeded" />
  <!-- Header -->
  <template v-if="this.client && this.found === true">
    <div class="container-fluid py-0 px-2">
      <froalaView v-model="pageData"></froalaView>
    </div>

    <!-- Page Content -->
    <div class="container px-2">
      <!-- Heading Row -->
      <div v-if="this.event && this.client" class="row align-items-top my-5">
        <div class="col-lg-5">
          <b-img class="mb-4 mt-2 mb-lg-0" :src="this.event.module.image" fluid rounded alt="Class image"></b-img>
          <h6 class="mt-2">{{ $t('registration.needToKnowMore') }}</h6>
          <hr class="pt-0 pb-0 mt-0 mb-1">
          <address>
            <strong>{{this.client.name}}</strong><br/>
            <span v-if="this.client.contactname">{{this.client.contactname}}<br/></span>
            <span v-if="this.client.street">{{this.client.street}}<br/></span>
            <span v-if="this.client.citystatezip">{{this.client.citystatezip}}<br/></span>
            <span v-if="this.client.phone">{{this.client.phone}}<br/></span>
            <a :href="'mailto:' + this.client.email">{{this.client.email}}</a>
          </address>
        </div>
        <div class="col-lg-7" data-cy="EnrollmentPanel">
          <h1 class="font-weight-strong">{{ this.event.title }}</h1>
          <div v-html="this.event.module.description"></div>
          <p class="mt-2" v-if="event.paymentRequired">
            <strong>{{ $t('registration.eventCost') }}:</strong>
            <br /><span data-cy="EventCost">{{formatCurrency(event.price)}}</span>
          </p>
          <p v-if="!event.evergreen" class="mt-2">
            <strong>{{ $t('registration.eventDate') }}:</strong>
            <br />{{ $dayjs(this.event.advertisedDate).format('MMM Do, YYYY')}} <small>({{ this.event.timeZoneAbbr }})</small>
          </p>
          <template v-if="!this.event.registrationClosed && !this.event.full">
            <div v-if="!isEnrolled">
              <template v-if="isLoggedIn">
                <!-- Enrollment Questions  -->
                <b-form @submit.stop.prevent="processRegistrationForm">
                  <div v-if="this.event.questions && this.event.questions.length > 0">
                    <b-form-group v-for="(question, index) in this.event.questions" :key="index" :label="question.question" :label-for="'input-question-' + index">
                      <b-input-group class="mb-3">
                        <b-form-input
                          :id="'input-question-' + index"
                          :data-cy="'input-' + index"
                          v-model="question.answer"
                          maxlength="255"
                          :required="question.required"
                        ></b-form-input>
                      </b-input-group>
                    </b-form-group>
                  </div>
                  <template v-if="event.paymentRequired && !isUserEventAdmin">
                    <b-button v-if="!busy" data-cy="btnEnroll" type="submit" id="btnEnroll" variant="success">Check Out</b-button>
                    <b-button v-if="busy" data-cy="btnEnroll" type="submit" id="btnEnroll" variant="secondary" disabled>Check Out... <b-spinner small></b-spinner></b-button>
                  </template>
                  <template v-else>
                    <b-button v-if="!busy" data-cy="btnEnroll" type="submit" id="btnEnroll" variant="success">Enroll</b-button>
                    <b-button v-if="busy" data-cy="btnEnroll" type="submit" id="btnEnroll" variant="secondary" disabled>Enrolling... <b-spinner small></b-spinner></b-button>
                  </template>
                  <div v-if="event.paymentRequired && isUserEventAdmin" data-cy="PaymentNotRequired" class="small">Payment not required for Subscribers and Admins</div>
                </b-form>
              </template>
              <template v-else>
                <p>{{$t('registration.freeAccountMessage', [client.name]) }}</p>
                <b-button data-cy="btnLogin" variant="success" v-b-modal.modal-login href="#">{{$t('registration.loginToAccount')}}</b-button>
                <b-button data-cy="btnRegister" class="ml-2" variant="success" v-b-modal.modal-register href="#">{{$t('registration.registerForAccount')}}</b-button>
                <!-- <span v-if="this.event.full" class="text-danger ml-3">Sorry, this event is full.</span> -->
              </template>
            </div>
            <div v-else>
              <b-alert show variant="success"><h4>{{$t('registration.youAreEnrolled')}}</h4></b-alert>
              <p v-html="$t('registration.nowWhat', [event.title])"></p>
              <template v-if="this.event.isFutureEvent && !this.event.evergreen">
                <p>{{$t('registration.eventStartsMessage')}}</p>
              </template>
              <template v-else>
                <b-link href="#" variant="primary" :to="{name: 'event', params: {eventId: this.event.id}}">{{ $t('registration.goToEvent') }}</b-link><br>
              </template>
              <router-link :to="{ name: 'home'}">{{ $t('registration.seeEnrolledEvents') }}</router-link><br>
              <b-link id="btnCancelEnroll"  @click="cancelEnrollment">{{ $t('registration.cancelMyEnrollment') }}</b-link>
              <br>
            </div>
          </template>
          <template v-else>
            <div v-if="this.event.registrationClosed">
              <b><em>Registration for this Event has Ended</em></b>
            </div>
            <div v-else-if="this.event.full">
              <b><em>Sorry, this event is full.</em></b>
            </div>
            <froalaView v-model="event.closedMessage" class="clearfix"></froalaView>
          </template>
        </div>
      </div>
    </div>
  </template>
  <template v-if="found === false">
    <FourOhFour />
  </template>
</div>
</template>

<script>
import LoginModal from '@/components/LoginModal.vue'
import RegisterModal from '@/components/RegisterModal.vue'
import FourOhFour from '@/components/404.vue'
import { clientServices, eventServices } from '@/_services'
import { loadStripe } from '@stripe/stripe-js'

export default {
  components: {
    LoginModal,
    RegisterModal,
    FourOhFour
  },
  data () {
    return {
      client: null,
      pageData: '',
      username: '',
      event: null,
      hasRegistration: null,
      host: process.env.VUE_APP_API_HOST,
      imgHost: process.env.VUE_APP_IMG_HOST,
      found: null,
      busy: false,
      stripe: {
        init: {},
        publishableKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY
      },
      isUserEventAdmin: false
    }
  },
  methods: {
    loginSucceeded () {
      this.getUserEnrollmentStatus()
    },
    registrationSucceeded () {
      // new user registration, so no need to get Event Enrollment Status
    },
    getUserEnrollmentStatus () {
      eventServices.isRegisteredForEvent(this.event.id, this.$store.getters.userProfile.id)
        .then((resp) => {
          console.log(resp)
          this.hasRegistration = resp.isRegistered
          this.isUserEventAdmin = resp.isAdmin
        })
        .catch(err => {
          console.log(err)
        })
    },
    openRegistrationModal () {
      // get a ref to the component, then to the modal
      this.$refs['r-RegisterModal'].$refs['register-modal'].show()
    },
    processRegistrationForm () {
      if (this.event.paymentRequired && !this.isUserEventAdmin) {
        this.startPaymentForEvent()
      } else {
        this.enrollUser()
      }
    },
    startPaymentForEvent () {
      eventServices.getPaymentSessionEvent(this.event.id, this.event.questions, this.$store.getters.userProfile.id)
        .then(result => {
          console.log('Response: ' + JSON.stringify(result))
          // payment is required for this event so let's redirect the user
          var init = loadStripe(this.stripe.publishableKey, { stripeAccount: result.account })
          init.then(stripe => {
            stripe.redirectToCheckout({ sessionId: result.id })
          })
            .then(result => {
              // If `redirectToCheckout` fails due to a browser or network
              // error, you should display the localized error message to your
              // customer using `error.message`.
              if (result.error) {
                alert(result.error.message)
              }
            })
        })
        .catch(err => {
          if (err.status === 290) {
            this.event.full = true
          }
          console.log(err)
          this.busy = false
        })
    },
    enrollUser () {
      this.busy = true
      console.log('Enroll User: ' + this.$store.getters.userProfile.name)
      eventServices.registerForEvent(this.event.id, this.event.questions, this.$store.getters.userProfile.id)
        .then(response => {
          console.log('Response: ' + JSON.stringify(response.status))
          if (response.status === 290) {
            console.log('Full')
            this.event.full = true
          } else {
            this.hasRegistration = true
          }
          this.busy = false
        })
        .catch(err => {
          console.log(err.response.data)
          this.busy = false
        })
    },
    cancelEnrollment () {
      console.log('Cancel Enrollment: ' + this.$store.getters.userProfile.name)
      var msg = 'Are you sure you want to cancel your enrollment?'
      if (this.event.paymentRequired) {
        msg = `Refunds are not automatically processed.  If you wish to request a refund, you need to contact ${this.client.contactname}.<br />${msg}`
      }
      this.confirmContinue('Continue?', msg)
        .then(answerYes => {
          if (answerYes) {
            eventServices.unregisterFromEvent(this.event.id, this.$store.getters.userProfile.id)
              .then(() => {
                console.log('Cancel Enrollment!')
                this.hasRegistration = false
                // this.$store.dispatch('removeRegistration', data)
              })
              .catch(err => {
                console.log(err)
              })
          }
        })
    },
    formatCurrency (value) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'usd',
        minimumFractionDigits: 2
      })
      return formatter.format(value)
    },
    confirmContinue (title, message) {
      const h = this.$createElement
      const messageVNode = h('div', { domProps: { innerHTML: message } })
      return new Promise((resolve, reject) => {
        this.$bvModal.msgBoxConfirm([messageVNode], {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'success',
          okTitle: 'YES',
          cancelTitle: 'NO',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        })
          .then(answer => {
            resolve(answer)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    getClientRegistrationPage () {
      clientServices.getClientPage(this.client.id, 'RegistrationPage')
        .then(data => {
          this.pageData = data.pageData
        })
    }
  },
  computed: {
    isLoggedIn () {
      return this.$store.getters.isLoggedIn
    },
    isEnrolled () {
      if (this.isLoggedIn) {
        return this.hasRegistration
      } else {
        return false
      }
    }
  },
  created () {
    this.username = this.$route.params.clientname
    const eventId = this.$route.params.eventId
    clientServices.getByUsername(this.username)
      .then(data => {
        if (data) {
          // console.log('Client: ', data)
          this.client = data // this does not get the full client details

          this.getClientRegistrationPage()

          clientServices.getEvent(this.client.id, eventId)
            .then(data => {
              if (data) {
                this.event = data
                console.log('Event Data: ', data)
                this.found = true
                // console.log('Event: ', data)
                if (this.isLoggedIn) {
                // ******************************************************
                // *** session_id is returned after the event is paid ***
                // ******************************************************
                  if (this.$route.query.session_id) {
                    eventServices.confirmEventRegistration(this.client.id, this.$route.query.session_id)
                      .then((resp) => {
                        this.hasRegistration = resp
                      })
                      .catch(err => {
                        console.log(err)
                      })
                  } else {
                    this.getUserEnrollmentStatus()
                  }
                }
              } else {
                this.found = false
              }
            })
            .catch(err => {
              console.log(err)
              this.found = false
            })
        }
      })
      .catch(err => {
        console.log(JSON.stringify(err))
        this.found = false
      })
  }
}
</script>
